/**
 * Black Tulip v3 — the named delivery date. 05 §H.
 *
 * Client-side on purpose: the date depends on when the page is *read*, and a
 * Liquid-rendered date would be wrong for anyone whose page was cached, which
 * on a Shopify storefront is most people.
 *
 *   dispatch  order day if before the cut-off Mon–Fri, else the next business day
 *   standard  dispatch + 4 business days   (the later bound of 2–4 — under-promise)
 *   express   dispatch + 2 business days
 *
 * Weekends and UK bank holidays are skipped. Everything is computed in
 * Europe/London, never in the reader's zone: a customer in Sydney gets the
 * date the warehouse will actually work to.
 *
 * Renders NOTHING if it cannot be certain — past the end of the holiday table,
 * or if the element carries no cut-off. A wrong arrival date is worse than no
 * arrival date, and the empty-token rule applies to computed values too.
 */
(function () {
  'use strict';

  // ONCE PER PAGE, HOWEVER MANY TIMES IT IS LOADED — 26 September 2026.
  // Five sections emit this script: the landing page, the cart, the product
  // tabs, the returns page, and the cart drawer — and the drawer is rendered
  // by layout/theme.liquid on every page, so any page carrying one of the
  // other four loaded it twice. The browser fetches one URL once, but the IIFE
  // ran twice and reassigned window.BTDate. Harmless today; not a thing to
  // leave for the day this file gains a listener.
  //
  // The tags are NOT removed from the sections. The drawer only reaches pages
  // on layout/theme.liquid, and a section rendered under another layout would
  // silently lose its date. Each section stays self-sufficient; a second load
  // becomes a no-op instead.
  if (window.BTDate) return;

  // GOV.UK England & Wales bank holidays. This table is the reason the feature
  // can be trusted, and the reason it expires: past the last date below the
  // date is not computed at all rather than computed wrongly.
  var HOLIDAYS = [
    '2026-01-01','2026-04-03','2026-04-06','2026-05-04','2026-05-25',
    '2026-08-31','2026-12-25','2026-12-28',
    '2027-01-01','2027-03-26','2027-03-29','2027-05-03','2027-05-31',
    '2027-08-30','2027-12-27','2027-12-28'
  ];
  var HOLIDAY = Object.create(null);
  for (var h = 0; h < HOLIDAYS.length; h++) HOLIDAY[HOLIDAYS[h]] = true;
  var TABLE_ENDS = HOLIDAYS[HOLIDAYS.length - 1];

  var DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  var MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];

  /** Wall-clock date and time in Europe/London, whatever the reader's zone. */
  function londonNow() {
    var parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false
    }).formatToParts(new Date());
    var f = {};
    for (var i = 0; i < parts.length; i++) f[parts[i].type] = parts[i].value;
    return {
      // A UTC date object standing for a London calendar day: arithmetic on it
      // is DST-free, which date arithmetic in a local zone is not.
      day: new Date(Date.UTC(+f.year, +f.month - 1, +f.day)),
      minutes: (+f.hour) * 60 + (+f.minute)
    };
  }

  function iso(d) {
    return d.getUTCFullYear() + '-' +
      String(d.getUTCMonth() + 1).padStart(2, '0') + '-' +
      String(d.getUTCDate()).padStart(2, '0');
  }
  function isBusinessDay(d) {
    var wd = d.getUTCDay();
    return wd !== 0 && wd !== 6 && !HOLIDAY[iso(d)];
  }
  function addDays(d, n) {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + n));
  }
  function nextBusinessDay(d) {
    var x = addDays(d, 1);
    while (!isBusinessDay(x)) x = addDays(x, 1);
    return x;
  }
  function addBusinessDays(d, n) {
    var x = d;
    for (var i = 0; i < n; i++) x = nextBusinessDay(x);
    return x;
  }

  function parseCutoff(v) {
    var m = /^(\d{1,2}):(\d{2})$/.exec(String(v || ''));
    if (!m) return null;
    var mins = (+m[1]) * 60 + (+m[2]);
    return mins >= 0 && mins < 1440 ? mins : null;
  }

  function arrivalFor(el) {
    var cutoff = parseCutoff(el.dataset.dispatchCutoff);
    if (cutoff === null) return null;
    var days = parseInt(el.dataset.standardDays, 10);
    if (!isFinite(days) || days < 1) return null;

    var now = londonNow();

    // Past the holiday table, no date is offered rather than a wrong one.
    if (iso(now.day) > TABLE_ENDS) return null;

    var dispatch = (isBusinessDay(now.day) && now.minutes < cutoff)
      ? now.day
      : nextBusinessDay(now.day);

    var arrival = addBusinessDays(dispatch, days);
    if (iso(arrival) > TABLE_ENDS) return null;   // a skipped holiday we do not know about
    return arrival;
  }

  // The sentence comes from the locale, through data-template, because the
  // approved string lives there and a second copy in this file is a second
  // version of it. The literal below is a fallback, not the source.
  var DEFAULT_TEMPLATE = 'Arrives by {day} {date}';

  // Both spellings of a placeholder: the doubled-brace form the locale file
  // writes, with or without inner spaces, and the bare form this script has
  // always used. Accepting both means the Liquid side never has to rewrite the
  // translation to suit the script — and rewriting it was what got the snippet
  // silently rejected by Shopify's GitHub sync on 16 September.
  function ph(name) {
    return new RegExp('\\{\\{\\s*' + name + '\\s*\\}\\}|\\{' + name + '\\}', 'g');
  }
  var DAY_PH = ph('day');
  var DATE_PH = ph('date');

  /**
   * A template is only used if it has somewhere to put the day AND the date.
   * Anything else — an empty attribute, a translation whose interpolations were
   * stripped, a locale someone edited without the placeholders — falls back to
   * the literal above. The failure mode this avoids is the one worth avoiding:
   * a confident sentence with the date missing out of the middle of it.
   */
  function templateFor(el) {
    var t = el.dataset.template;
    if (!t) return DEFAULT_TEMPLATE;
    DAY_PH.lastIndex = 0; DATE_PH.lastIndex = 0;
    return (DAY_PH.test(t) && DATE_PH.test(t)) ? t : DEFAULT_TEMPLATE;
  }

  function render(el) {
    var d = arrivalFor(el);
    if (!d) { el.textContent = ''; el.hidden = true; return; }
    DAY_PH.lastIndex = 0; DATE_PH.lastIndex = 0;
    el.textContent = templateFor(el)
      .replace(DAY_PH, DAYS[d.getUTCDay()])
      .replace(DATE_PH, d.getUTCDate() + ' ' + MONTHS[d.getUTCMonth()]) + '. ';
    el.hidden = false;
  }

  function boot() {
    var els = document.querySelectorAll('[data-v3-named-date]');
    for (var i = 0; i < els.length; i++) render(els[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // The theme editor re-renders a section in place and does not re-run its
  // scripts, so a date span it brings back arrives empty. Say it again.
  document.addEventListener('shopify:section:load', boot);

  // render and renderAll are PUBLIC — 26 September. Content that arrives after
  // boot needs them: the cart drawer swaps its HTML on every change, and the
  // server renders the date span empty because the date depends on when the
  // page is read. v3-cart.js used to re-check those spans only to HIDE the ones
  // with no date — it never filled in the ones that had one, so the drawer's
  // "Arrives by" was blank after every quick-add. Found while verifying the
  // once-per-page guard at the top of this file, which also means a second
  // load of the script can no longer paper over it.
  window.BTDate = {
    render: render, renderAll: boot,
    _templateFor: templateFor, _default: DEFAULT_TEMPLATE,
    _londonNow: londonNow, _isBusinessDay: isBusinessDay,
    _addBusinessDays: addBusinessDays, _nextBusinessDay: nextBusinessDay,
    _iso: iso, _tableEnds: TABLE_ENDS, arrivalFor: arrivalFor
  };
})();
